/**
 * NeonSign — the neon sign «Живой значок» over a turntable in the half-dark. Task 68.
 *
 * `docs/superpowers/specs/2026-10-25-oblik-afisha-design.md` «Неоновая вывеска»: the five places
 * that will fill — «Поиск» before anything is typed, an empty «Избранное», «Недавние» and «Новое»,
 * and the gate «Только для подписчиков». The owner chose it on 2026-10-24 and 2026-10-25 from the
 * pictures and the video in `docs/design/2026-10-25-afisha/`. A load, a failure and «nothing
 * found» never show it: it means «here it comes», as the dimmed logo did before it (task 41).
 *
 * ## Drawn twice
 *
 * The unlit tubes, and the lit copy over them. Only the lit copy's opacity flickers as the sign
 * comes on, so the turn-on is the same for every shape of sign. The parts that move — the
 * magnifier, the hourglass, the heart, the headphones, the lock — move in both copies on the same
 * clock (the same classes, mounted at once), or the dim copy would show through as a ghost.
 *
 * A screen reader hears the words once: the dim copy is the heading, named by its words, and the
 * lit copy is hidden from it, like the background.
 *
 * ## The background
 *
 * The deck of «Сейчас играет» — `assets/deck.svg` with `assets/platter.svg` turning on it, once in
 * five seconds — the pictures the player loads anyway, not the 400-KB snapshot of the mockup.
 * At 33⅓ the print on the mat «stood still» on a 30-frame video; five seconds is slow enough not
 * to. Every length is `styles/neon.css`'s, in points of the mockup's 398-point box.
 */

import type { JSX } from 'react';

import deckUrl from '../assets/deck.svg';
import platterUrl from '../assets/platter.svg';
import '../styles/neon.css';

/** The five places that have a sign: «Поиск», «Избранное», «Недавние», «Новое», the gate. */
export type NeonKind = 'poisk' | 'izbrannoe' | 'nedavnie' | 'novoe' | 'podpiska';

/** What each sign says — its heading for a screen reader, and what a test looks for. */
export const NEON_WORDS: Readonly<Record<NeonKind, string>> = Object.freeze({
  poisk: 'Что ищем?',
  izbrannoe: 'Любимое. Пока пусто',
  nedavnie: 'Что включим? Пока тихо',
  novoe: 'Скоро. Новое за неделю',
  podpiska: 'Только для подписчиков',
});

function SearchIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 66 66" className="neonIcon neonIconPoisk">
      <g className="neonOrbit">
        <g className="neonY" fill="none" stroke="currentColor" strokeLinecap="round">
          <circle cx="27" cy="27" r="21" strokeWidth="4" />
          <g className="neonDisc">
            <path d="M27 13 A14 14 0 0 1 41 27" strokeWidth="2.4" />
            <path d="M27 41 A14 14 0 0 1 13 27" strokeWidth="2.4" />
          </g>
          <circle cx="27" cy="27" r="3.5" strokeWidth="3" />
          <path d="M43 43 L60 60" strokeWidth="7" />
        </g>
      </g>
    </svg>
  );
}

function HeartIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 70 64" className="neonIcon neonIconHeart">
      <g className="neonBeat">
        <path
          className="neonY"
          d="M35 58 C14 44 5 32 5 20 C5 11 12 5 20 5 C27 5 32 9 35 15 C38 9 43 5 50 5 C58 5 65 11 65 20 C65 32 56 44 35 58 Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="4.5"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

function HeadphonesIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 72 66" className="neonIcon neonIconPhones">
      <g className="neonBop">
        <path
          className="neonG"
          d="M10 42 V34 A26 26 0 0 1 62 34 V42"
          fill="none"
          stroke="currentColor"
          strokeWidth="4.5"
          strokeLinecap="round"
        />
        <g className="neonY" fill="none" stroke="currentColor" strokeWidth="4.5">
          <rect x="4" y="38" width="13" height="23" rx="5" />
          <rect x="55" y="38" width="13" height="23" rx="5" />
        </g>
      </g>
    </svg>
  );
}

/** The sand's two shapes mirror each other round the neck, so the glass turned over is the start. */
function HourglassIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 60 80" className="neonIcon neonIconGlass">
      <g className="neonGlass">
        <g
          className="neonG"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
        >
          <path d="M15 9 Q15 30 28 40 Q15 50 15 71" />
          <path d="M45 9 Q45 30 32 40 Q45 50 45 71" />
        </g>
        <g className="neonY" stroke="currentColor" strokeLinecap="round">
          <path d="M7 6 H53 M7 74 H53" strokeWidth="5" />
          <path className="neonStream" d="M30 41 V64" strokeWidth="2" />
        </g>
        <g className="neonY" fill="currentColor">
          <path className="neonSandTop" d="M19 14 H41 L30 36 Z" />
          <path className="neonSandBottom" d="M19 66 H41 L30 44 Z" />
        </g>
      </g>
    </svg>
  );
}

function LockIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 64 76" className="neonIcon neonIconLock">
      <g className="neonLock">
        <g
          className="neonY"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path className="neonShackle" d="M17 33 V22 a15 15 0 0 1 30 0 V33" strokeWidth="5" />
          <rect x="7" y="33" width="50" height="38" rx="9" strokeWidth="5" />
          <path d="M32 47 v10" strokeWidth="5" />
        </g>
      </g>
    </svg>
  );
}

/** One copy of the sign. Drawn twice: dim, and lit over it. */
function Face({ kind }: { readonly kind: NeonKind }): JSX.Element {
  switch (kind) {
    case 'poisk':
      return (
        <div className="neonRow">
          <SearchIcon />
          <span className="neonWords neonY">
            Что ищем<span className="neonBlink">?</span>
          </span>
        </div>
      );
    case 'izbrannoe':
      return (
        <div className="neonRow">
          <HeartIcon />
          <div>
            <div className="neonWords neonY">Любимое</div>
            <div className="neonSub neonG neonLater">пока пусто</div>
          </div>
        </div>
      );
    case 'nedavnie':
      return (
        <div className="neonRow">
          <HeadphonesIcon />
          <div>
            <div className="neonWords neonY">
              Что включим<span className="neonBlink">?</span>
            </div>
            <div className="neonSub neonG neonLater">пока тихо</div>
          </div>
        </div>
      );
    case 'novoe':
      return (
        <div className="neonRow">
          <HourglassIcon />
          <div>
            <div className="neonWords neonY">
              Ск<span className="neonDead">о</span>ро
            </div>
            <div className="neonSub neonG neonLater">новое за неделю</div>
          </div>
        </div>
      );
    case 'podpiska':
      return (
        <>
          <div className="neonRow neonColumn">
            <LockIcon />
            <div className="neonWords neonY">
              Только для
              <br />
              подписчиков
            </div>
          </div>
          <div className="neonChev">
            <i className="neonG" />
            <i className="neonG" />
            <i className="neonG" />
          </div>
        </>
      );
  }
}

export function NeonSign({
  kind,
  level = 2,
}: {
  readonly kind: NeonKind;
  /** The heading's level: 1 on the gate, where the sign is the screen's title. */
  readonly level?: 1 | 2;
}): JSX.Element {
  return (
    <div className="neon" data-kind={kind}>
      <div className="neonDeck" aria-hidden="true">
        <img className="neonDeckBody" src={deckUrl} alt="" draggable={false} />
        <img className="neonPlatter" src={platterUrl} alt="" draggable={false} />
      </div>
      <div className="neonVignette" aria-hidden="true" />
      <div className="neonSpill" aria-hidden="true" />
      <div className="neonSign">
        <div className="neonLayers">
          <div className="neonOff" role="heading" aria-level={level} aria-label={NEON_WORDS[kind]}>
            <div aria-hidden="true">
              <Face kind={kind} />
            </div>
          </div>
          <div className="neonOn" aria-hidden="true">
            <Face kind={kind} />
          </div>
        </div>
      </div>
    </div>
  );
}
